import { useCallback, useEffect, useState } from "react"

import { getApiHelper, postApiHelper } from "../../util/apiHelpers.js"

const ENDPOINT = "fortellis-historical-ingestion"

const ACTIVE_STATUSES = ["PendingApproval", "Approved", "Processing"]

const STATUS_LABELS = {
  PendingApproval: "Waiting for approval",
  Approved: "Approved — runs on the next scheduled task",
  Processing: "Running now",
  Completed: "Completed",
  Failed: "Failed",
  Rejected: "Not approved",
}

const STATUS_STYLES = {
  PendingApproval: "bg-[#FEF0C7] text-[#B54708]",
  Approved: "bg-[#D1FADF] text-[#027A48]",
  Processing: "bg-[#D1E9FF] text-[#175CD3]",
  Completed: "bg-[#D1FADF] text-[#027A48]",
  Failed: "bg-[#FEE4E2] text-[#B42318]",
  Rejected: "bg-[#F2F4F7] text-[#475467]",
}

const errorMessage = (result, fallback) =>
  result?.response?.data?.message || result?.message || fallback

/**
 * Lets the dealership ask for a historical Fortellis pull. The request itself never calls
 * Fortellis; it only notifies the approver, who decides whether the cost is accepted.
 */
export default function HistoricalIngestionRequest({ dealerID, fortellisID }) {
  const [request, setRequest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  const loadLatest = useCallback(async () => {
    if (!dealerID) {
      setLoading(false)
      return
    }
    setLoading(true)
    const result = await getApiHelper(`${ENDPOINT}/dealer/${dealerID}/latest`)
    if (result?.status === 200) {
      setRequest(result.data)
    } else if (result?.status === 204) {
      setRequest(null)
    }
    setLoading(false)
  }, [dealerID])

  useEffect(() => {
    loadLatest()
  }, [loadLatest])

  const submit = async () => {
    setSubmitting(true)
    setError("")
    const result = await postApiHelper(`${ENDPOINT}/request`, {
      dealerId: Number(dealerID),
    })
    setSubmitting(false)

    if (result?.status === 200 || result?.status === 201) {
      setRequest(result.data)
      setConfirming(false)
      return
    }
    setError(errorMessage(result, "The request could not be created."))
  }

  const isActive = request && ACTIVE_STATUSES.includes(request.status)
  const missingFortellisID = !fortellisID || String(fortellisID).trim() === ""

  return (
    <div className="grid grid-cols-12 mb-[12px] justify-start w-full items-start">
      <h3 className="grid col-span-2 font-bold text-[#586283]">
        Historical data
      </h3>
      <div className="grid col-span-10 ml-[15px] flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setError("")
              setConfirming(true)
            }}
            disabled={loading || isActive || missingFortellisID}
            title={
              missingFortellisID
                ? "Save a Fortellis ID for this dealership first"
                : isActive
                ? "A request is already in progress for this dealership"
                : "Ask for approval to pull historical Fortellis data"
            }
            className="rounded-lg px-4 py-2 text-[12px] font-semibold border border-[#298FC2] text-[#298FC2] hover:bg-[#298FC2] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Request historical data ingestion
          </button>
          {!loading && request && (
            <span
              className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                STATUS_STYLES[request.status] || "bg-[#F2F4F7] text-[#475467]"
              }`}
            >
              {STATUS_LABELS[request.status] || request.status}
            </span>
          )}
        </div>

        {missingFortellisID && (
          <span className="text-[11px] text-[#B54708] pl-1">
            Add and save a Fortellis ID before requesting historical data.
          </span>
        )}

        {!loading && request && (
          <span className="text-[11px] text-[#667085] pl-1">
            Requested {formatDate(request.requestedAt)} by{" "}
            {request.requesterEmail}
            {request.decidedAt && ` · decided ${formatDate(request.decidedAt)}`}
            {request.status === "Rejected" &&
              request.rejectionReason &&
              ` · reason: ${request.rejectionReason}`}
            {request.status === "Failed" && " · operations has been notified"}
          </span>
        )}

        {error && (
          <span className="text-[11px] text-red-500 font-medium pl-1">
            {error}
          </span>
        )}
      </div>

      {confirming && (
        <ConfirmDialog
          submitting={submitting}
          error={error}
          onCancel={() => setConfirming(false)}
          onConfirm={submit}
        />
      )}
    </div>
  )
}

function ConfirmDialog({ submitting, error, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl w-[520px] max-w-[92vw] p-6 shadow-xl">
        <h3 className="font-bold text-[#586283] text-[18px] mb-3">
          Request historical data ingestion
        </h3>
        <p className="text-[13px] text-[#475467] mb-3">
          This sends an approval request for a one-time pull of this
          dealership&apos;s historical F&amp;I Sales and Repair Orders. Nothing
          is requested from Fortellis until the request is approved.
        </p>
        <ul className="text-[13px] text-[#475467] list-disc pl-5 mb-4 space-y-1">
          <li>Scope: about 5 years of F&amp;I Sales and Repair Orders</li>
          <li>
            The approver sees the estimated number of API calls and the
            estimated cost before deciding
          </li>
          <li>
            Once approved, it runs automatically on the next scheduled
            historical task
          </li>
        </ul>
        {error && (
          <p className="text-[12px] text-red-500 font-medium mb-3">{error}</p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-[12px] font-semibold border border-[#586283] text-[#586283] hover:bg-[#586283] hover:text-white disabled:opacity-40 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-[12px] font-semibold text-white bg-[#298FC2] hover:bg-[#1f7aa8] disabled:opacity-40 transition-colors"
          >
            {submitting ? "Sending..." : "Send request for approval"}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatDate(value) {
  if (!value) return ""
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString()
}
